// Import the functions you need from the SDKs you need
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  Auth,
  connectAuthEmulator,
} from "firebase/auth";
import {
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
  Firestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  enableNetwork,
  disableNetwork,
  memoryLocalCache,
  getFirestore,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
// Import our service worker manager utilities
import {
  detectServiceWorkerIssues,
  markServiceWorkersDisabled,
  resetBrowserState,
  safeGuardPage,
} from "@shared/utils/serviceWorkerManager";
// Import Capacitor for platform detection
import { Capacitor } from "@capacitor/core";

// Apply safe guards for this page
safeGuardPage();

// Function to handle errors when environment variables are missing
const logEnvError = (varName: string) => {
  // Use safe error logging to prevent sensitive data leakage
  import("../../../shared/utils/errorSanitizer").then(
    ({ safeConsoleError }) => {
      safeConsoleError(`Missing environment variable: ${varName}`);
    }
  );
  return "";
};

// Function to get environment variables with fallback values for production
const getEnvVar = (name: string) => {
  const value = import.meta.env[name];
  if (!value) {
    logEnvError(name);
    return "";
  }
  return value;
};

// Your web app's Firebase configuration with fallbacks for production
// These values will be used if the env vars aren't set properly
const firebaseConfig = {
  apiKey: getEnvVar("VITE_APP_FIREBASE_API_KEY"),
  authDomain: getEnvVar("VITE_APP_FIREBASE_AUTH_DOMAIN"),
  projectId: getEnvVar("VITE_APP_FIREBASE_PROJECT_ID"),
  storageBucket: getEnvVar("VITE_APP_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: getEnvVar("VITE_APP_FIREBASE_MESSAGING_SENDER_ID"),
  appId: getEnvVar("VITE_APP_FIREBASE_APP_ID"),
  measurementId: getEnvVar("VITE_APP_FIREBASE_MEASUREMENT_ID"),
};

// Check if service worker has had issues with Firestore
const hasServiceWorkerIssues = detectServiceWorkerIssues();

// Initialize Firebase (check if already initialized to avoid duplicates)
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize App Check for security
let appCheck;
try {
  // Only initialize App Check in production or when VITE_RECAPTCHA_SITE_KEY is available
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (recaptchaSiteKey && import.meta.env.PROD) {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    import("../../../shared/utils/errorSanitizer").then(
      ({ safeConsoleLog }) => {
        safeConsoleLog("✅ App Check initialized successfully");
      }
    );
  } else {
    // Enable debug tokens in development
    if (!import.meta.env.PROD) {
      (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
  }
} catch (error) {
  import("../../../shared/utils/errorSanitizer").then(
    ({ safeConsoleError }) => {
      safeConsoleError("❌ Error initializing App Check:", error);
    }
  );
}

// Initialize Auth with platform-specific configuration
let auth: Auth;
try {
  if (Capacitor.isNativePlatform()) {
    // For native mobile platforms (iOS/Android), use initializeAuth with indexedDBLocalPersistence
    auth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    // For web platforms, use standard getAuth
    auth = getAuth(app);
  }
} catch (error) {
  import("../../../shared/utils/errorSanitizer").then(
    ({ safeConsoleError }) => {
      safeConsoleError("❌ Error initializing Firebase Auth:", error);
    }
  );
  // Fallback to web auth if native initialization fails
  auth = getAuth(app);
}

// Initialize Firestore and declare DB variable with proper typing
let db: Firestore;

// Simplified initialization - avoid nested try-catch to prevent double initialization
if (hasServiceWorkerIssues) {
  // Use memory-only cache with no persistence to avoid service worker conflicts
  db = initializeFirestore(app, {
    experimentalForceLongPolling: true,
    localCache: memoryLocalCache(),
  });
} else {
  // Try persistent cache first, fall back to memory cache
  try {
    db = initializeFirestore(app, {
      experimentalForceLongPolling: true,
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
        cacheSizeBytes: CACHE_SIZE_UNLIMITED,
      }),
    });
  } catch (error) {
    // If initialization fails (e.g., already initialized or persistence error)
    // try to get the existing instance first
    let gotExistingInstance = false;
    try {
      db = getFirestore(app);
      gotExistingInstance = true;
    } catch (getError) {
      // Last resort: try memory cache
      try {
        db = initializeFirestore(app, {
          experimentalForceLongPolling: true,
          localCache: memoryLocalCache(),
        });
      } catch (memError) {
        // Absolute last resort: use getFirestore again
        db = getFirestore(app);
        gotExistingInstance = true;
      }
    }

    // Only mark SW disabled if we couldn't get an existing instance
    // (which would indicate a real initialization problem, not just a double-init attempt)
    if (!gotExistingInstance) {
      markServiceWorkersDisabled();
    }
  }
}

// Error handler for Firestore connection issues
const handleFirestoreError = (error: any) => {
  // Use safe error logging to prevent sensitive data leakage
  import("../../../shared/utils/errorSanitizer").then(
    ({ safeConsoleError, shouldSuppressError }) => {
      if (!shouldSuppressError(error)) {
        safeConsoleError("Firestore connection error:", error);
      }
    }
  );

  // Check if it's related to service worker or network connections
  const errorMessage = error?.message || String(error);
  const isConnectionError =
    errorMessage.includes("ServiceWorker") ||
    errorMessage.includes("cache") ||
    errorMessage.includes("firestore") ||
    errorMessage.includes("Listen/channel") ||
    errorMessage.includes("NS_BINDING_ABORTED") ||
    errorMessage.includes("Write/channel") ||
    errorMessage.includes("connection");

  if (isConnectionError) {
    // Use safe error logging
    import("../../../shared/utils/errorSanitizer").then(
      ({ safeConsoleError }) => {
        safeConsoleError(
          "Detected Service Worker or connection issue with Firestore"
        );
      }
    );
    markServiceWorkersDisabled();

    // Clear any existing Firestore connections
    if (window.firestoreApp) {
      try {
        // Force terminate any existing Firestore instance
        window.firestoreApp.terminate?.();
      } catch (terminateError) {
        import("../../../shared/utils/errorSanitizer").then(
          ({ safeConsoleWarn }) => {
            safeConsoleWarn("Error terminating Firestore:", terminateError);
          }
        );
      }
    }

    // The current page may already be in an unrecoverable state
    // Let the user know they may need to refresh
    if (!window.firestoreErrorAlertShown) {
      window.firestoreErrorAlertShown = true;

      // Add a small delay to avoid immediate alert
      setTimeout(() => {
        resetBrowserState(false); // Don't show our own alert, we'll handle it here
        alert(
          "Connection issues detected. The page will reload to fix the problem."
        );
      }, 500);
    }
  }
};

// Set up global unhandled promise rejection handler instead of using internal Firestore API
window.addEventListener("unhandledrejection", (event) => {
  if (event.reason && typeof event.reason.message === "string") {
    const errorMsg = event.reason.message;
    if (
      (errorMsg.includes("firestore") ||
        errorMsg.includes("firebase") ||
        errorMsg.includes("cache") ||
        errorMsg.includes("Listen/channel") ||
        errorMsg.includes("Write/channel") ||
        errorMsg.includes("NS_BINDING_ABORTED")) &&
      (errorMsg.includes("ServiceWorker") ||
        errorMsg.includes("intercepted") ||
        errorMsg.includes("failed") ||
        errorMsg.includes("aborted") ||
        errorMsg.includes("terminated"))
    ) {
      handleFirestoreError(event.reason);
      // Prevent the error from propagating further
      event.preventDefault();
    }
  }
});

// Also handle general network errors that might affect Firestore
window.addEventListener("error", (event) => {
  const errorMsg = event.message || String(event.error);
  if (
    errorMsg.includes("NS_BINDING_ABORTED") ||
    errorMsg.includes("Write/channel") ||
    (errorMsg.includes("firestore") && errorMsg.includes("terminated"))
  ) {
    import("../../../shared/utils/errorSanitizer").then(
      ({ safeConsoleWarn, shouldSuppressError }) => {
        if (!shouldSuppressError({ message: errorMsg })) {
          safeConsoleWarn(
            "Network error detected that may affect Firestore:",
            errorMsg
          );
        }
      }
    );
    handleFirestoreError(event.error || new Error(errorMsg));
  }
});

// Force all network traffic to bypass service worker
window.addEventListener("error", (event) => {
  if (
    event.message &&
    (event.message.includes("ServiceWorker") ||
      event.message.includes("firestore"))
  ) {
    handleFirestoreError(new Error(event.message));
  }
});

// Initialize Firebase Functions (production only)
export const functions = getFunctions(app, "us-central1");
export const storage = getStorage(app);

const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "localhost" ||
    window.location.hostname.endsWith(".localhost"));

if (isLocalhost) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  try {
    connectStorageEmulator(storage, "127.0.0.1", 9199);
  } catch (e) {
    console.warn("Storage emulator connection failed", e);
  }
}

export { db };
export const analytics = getAnalytics(app);
export { auth, appCheck };
export default app;

// Add a flag to track if we've shown an error message
declare global {
  interface Window {
    firestoreErrorAlertShown?: boolean;
    firestoreApp?: any;
  }
}

// Function to check if Firebase is initialized and ready
export const isFirebaseInitialized = (): boolean => {
  try {
    // Check if the app is initialized
    if (!app) return false;

    // Check if Firestore is initialized
    if (!db) return false;

    // Check if auth is initialized
    if (!auth) return false;

    return true;
  } catch (error) {
    import("../../../shared/utils/errorSanitizer").then(
      ({ safeConsoleError }) => {
        safeConsoleError("Error checking Firebase initialization:", error);
      }
    );
    return false;
  }
};

// Function to reconnect to Firestore when connection issues occur
export const reconnectFirestore = async () => {
  try {
    if (navigator.onLine) {
      // Disable network first to reset connections
      await disableNetwork(db);
      import("../../../shared/utils/errorSanitizer").then(
        ({ safeConsoleLog }) => {
          safeConsoleLog("Firestore network disabled for reconnection");
        }
      );

      // Wait a bit before enabling again
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Re-enable network
      await enableNetwork(db);
      import("../../../shared/utils/errorSanitizer").then(
        ({ safeConsoleLog }) => {
          safeConsoleLog("Firestore network re-enabled");
        }
      );
      return true;
    }
    return false;
  } catch (error) {
    import("../../../shared/utils/errorSanitizer").then(
      ({ safeConsoleError }) => {
        safeConsoleError("Error reconnecting to Firestore:", error);
      }
    );
    return false;
  }
};
